import TenderAside from 'components/Organizations/TenderScreen/TenderAside';
import TenderFormTitle from 'components/Organizations/TenderScreen/TenderFormTitle';
import SeoWrapper from 'components/Seo/SeoWrapper';
import VehicleList from 'components/Vehicle/VehicleList/VehicleList';
import BBreadcrumb from 'components/common/BBreadcrumb';
import { Locale } from 'enums/locale';
import { checkAuthentication } from 'guards/checkAuthentication';
import { useBreadcrumbs } from 'hooks/useBreadcrumbs';
import { useLocale } from 'hooks/useLocale';
import { getSeoData } from 'lib/seo/seoData';
import type { GetServerSideProps, NextPage } from 'next';
import { useMemo } from 'react';
import {
  localizationService,
  SlugTranslations,
} from 'services/LocalizationService';
import { organizationService } from 'services/OrganizationService';
import { OrganizationDetailsForTenderResponse } from 'types/organization.type';
import { SeoProps } from 'types/seo.type';
import { DropdownOption } from 'types/types';

export const getServerSideProps: GetServerSideProps = checkAuthentication(
  async (ctx) => {
    const locale = ctx.locale as Locale;
    const slug = ctx.params?.slug as string;
    const organizationDetailsPromise =
      organizationService.getOrganizationDetailsForTender(ctx);

    const pathParent = ctx.locale === Locale.TR ? `hizmetler` : `services`;
    const seoPath = `/${pathParent}-${slug}`;
    const originalPath = `${
      ctx.locale === Locale.TR ? '/' : '/en/'
    }${pathParent}/${slug}`;

    const seoPromise = getSeoData(seoPath, locale, originalPath);

    const [organizationDetails, seoData] = await Promise.allSettled([
      organizationDetailsPromise,
      seoPromise,
    ]);

    let slugTranslations: SlugTranslations = {};

    if (organizationDetails.status === 'fulfilled') {
      if (!organizationDetails.value) {
        return {
          notFound: true,
        };
      }
      slugTranslations = { slug: organizationDetails.value.slugs };
      localizationService.slugTranslations = slugTranslations;
    }

    const pageNotFound = 'Page Not Found';
    if (
      seoData.status === 'fulfilled' &&
      organizationDetails.status === 'fulfilled' &&
      seoData.value.helmetSettings.title.startsWith(pageNotFound)
    ) {
      seoData.value.helmetSettings.meta = [
        ...(seoData.value.helmetSettings.meta || []).map((a) => {
          return {
            ...a,
            content: a.content.replaceAll(
              pageNotFound,
              organizationDetails.value.title
            ),
          };
        }),
        {
          name: 'description',
          content: organizationDetails.value.description,
        },
      ];
      seoData.value.helmetSettings.title =
        seoData.value.helmetSettings.title.replaceAll(
          pageNotFound,
          organizationDetails.value.title
        );
    }

    return {
      props: {
        ...(organizationDetails.status === 'fulfilled'
          ? { ...organizationDetails.value }
          : {}),
        ...(seoData.status === 'fulfilled' ? { ...seoData.value } : {}),
        slugTranslations,
      },
    };
  }
);

interface OrganizationDetailPageProps
  extends OrganizationDetailsForTenderResponse,
    SeoProps {
  harbors: DropdownOption<number>[];
  slugTranslations: SlugTranslations;
}

const OrganizationDetail: NextPage<OrganizationDetailPageProps> = ({
  title,
  description,
  slugs,
  slugTranslations,
  availableVehicles,
  content,
  helmetSettings,
  isSwimmingOrganization,
}) => {
  localizationService.slugTranslations = { ...slugTranslations };
  const { locale } = useLocale();
  const { generateOrganizationBreadcrumbs } = useBreadcrumbs();

  const hasH1TitleOnSeoContent = useMemo(() => {
    return !!content?.includes('</h1>');
  }, [content]);

  const breadcrumbs = generateOrganizationBreadcrumbs({
    isTranslatable: false,
    label: title,
    link: `/hizmetler/${slugs[locale]}`,
  });

  return (
    <SeoWrapper content={content} helmetSettings={helmetSettings}>
      <BBreadcrumb breadcrumbItems={breadcrumbs} wrapperClasses="mb-4" />
      <TenderFormTitle
        title={title}
        description={description}
        useHeaderTag={!hasH1TitleOnSeoContent}
      />

      <TenderAside isSwimmingOrganization={isSwimmingOrganization} />

      <VehicleList
        isThereMorePage={false}
        vehicles={availableVehicles}
        onPageChange={() => ({})}
      />
    </SeoWrapper>
  );
};

export default OrganizationDetail;
